/**
 * leadmanagement-teamshops — Root App.
 *
 * Routes:
 *   - "" (index)        -> Board
 *   - "dashboard"       -> DashboardPage
 *   - "settings"        -> SettingsPage (Listener-Status + Alt-Template-Editor)
 *   - "mail-vorlagen"   -> MailVorlagenPage (lazy-loaded, Phase 5.2 Plan 03 / D-03)
 *
 * MailVorlagenPage ist via React.lazy() geladen — TipTap-Bundle (~105 kB gzipped)
 * landet in einem eigenen dynamischen Chunk, nicht im initial Programm-Chunk.
 */

import { lazy, Suspense, useState } from 'react';
import { Phone } from 'lucide-react';
import { Routes, Route, Link, useLocation, useParams } from 'react-router-dom';
import TelefonatModal from './components/TelefonatModal';
import Board from './components/Board';
import SettingsPage from './pages/SettingsPage';
import DashboardPage from './pages/DashboardPage';

const MailVorlagenPage = lazy(() => import('./pages/MailVorlagenPage'));

export default function App() {
  const location = useLocation();
  const { programmeId } = useParams<{ programmeId: string }>();
  const base = `/programme/${programmeId}`;

  const [telefonatOpen, setTelefonatOpen] = useState(false);

  const isSettings = location.pathname.endsWith('/settings');
  const isDashboard = location.pathname.endsWith('/dashboard');
  const isMailVorlagen = location.pathname.endsWith('/mail-vorlagen');
  const isBoard = !isSettings && !isDashboard && !isMailVorlagen;

  const navLink = (active: boolean) =>
    active
      ? `inline-block px-3 py-1 text-sm font-semibold rounded bg-[#E9FF57] text-[#1A1A1A]`
      : `inline-block px-3 py-1 text-sm font-medium text-[#1A1A1A] hover:underline`;

  return (
    <div>
      <nav className="mb-4 flex gap-3 border-b border-[#DAD9D9] pb-2">
        <Link to={base} className={navLink(isBoard)}>
          Board
        </Link>
        <Link to={`${base}/dashboard`} className={navLink(isDashboard)}>
          Dashboard
        </Link>
        <Link to={`${base}/mail-vorlagen`} className={navLink(isMailVorlagen)}>
          Mail-Vorlagen
        </Link>
        <Link to={`${base}/settings`} className={navLink(isSettings)}>
          Einstellungen
        </Link>
        <button
          type="button"
          onClick={() => setTelefonatOpen(true)}
          className="ml-auto flex items-center gap-1.5 px-3 py-1 text-sm font-medium text-[#1A1A1A] border border-[#DAD9D9] rounded hover:bg-[#E9FF57] hover:border-[#1A1A1A] transition-colors"
        >
          <Phone className="w-4 h-4" />
          Telefonat erfassen
        </button>
      </nav>

      {telefonatOpen && (
        <TelefonatModal
          onClose={() => setTelefonatOpen(false)}
        />
      )}

      <Routes>
        <Route index element={<Board />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route
          path="mail-vorlagen"
          element={
            <Suspense fallback={<div className="p-6 text-sm text-gray-500">Lade Editor…</div>}>
              <MailVorlagenPage />
            </Suspense>
          }
        />
        <Route path="settings" element={<SettingsPage />} />
      </Routes>
    </div>
  );
}
